import React, { useMemo } from 'react';

interface ActivitySlot {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  instructorId: number;
  instructorName: string;
  topicMasterId: number;
  topicName: string;
  agenda: string;
  batchIds?: number[];
  zoomMeetingId: string | null;
  roomId?: number;
  roomName?: string;
  centreId?: number;
  centreName?: string;
}

interface BatchMapping {
  batchId: number;
  batchName: string;
  batchCode?: string;
}

interface TemplateAnalyticsProps {
  slots: ActivitySlot[];
  batchMappings: BatchMapping[];
  templateStartDate: string;
  templateEndDate: string;
}

interface AnalyticsData {
  totalSlots: number;
  totalHours: number;
  uniqueInstructors: number;
  uniqueTopics: number;
  uniqueBatches: number;
  uniqueRooms: number;
  uniqueCentres: number;
  uniqueDates: number;
  statusCounts: {
    upcoming: number;
    past: number;
  };
  instructorBreakdown: { id: number; name: string; count: number; hours: number }[];
  topicBreakdown: { id: number; name: string; count: number }[];
}

const TemplateAnalytics: React.FC<TemplateAnalyticsProps> = ({
  slots,
  batchMappings,
}) => {
  const analytics = useMemo<AnalyticsData>(() => {
    const today = new Date().toISOString().split('T')[0];
    const instructorMap = new Map<number, { name: string; count: number; hours: number }>();
    const topicMap = new Map<number, { name: string; count: number }>();
    const batchSet = new Set<number>();
    const roomSet = new Set<number>();
    const centreSet = new Set<number>();
    const dateSet = new Set<string>();
    let totalHours = 0;
    let upcoming = 0;
    let past = 0;

    for (const slot of slots) {
      // Hours
      if (slot.startTime && slot.endTime) {
        const [sh, sm] = slot.startTime.split(':').map(Number);
        const [eh, em] = slot.endTime.split(':').map(Number);
        const hours = (eh * 60 + em - sh * 60 - sm) / 60;
        if (hours > 0) totalHours += hours;
      }

      // Status based on date
      if (slot.date >= today) {
        upcoming++;
      } else {
        past++;
      }

      // Instructors
      if (slot.instructorId) {
        const existing = instructorMap.get(slot.instructorId);
        const slotHours = (() => {
          if (!slot.startTime || !slot.endTime) return 0;
          const [sh, sm] = slot.startTime.split(':').map(Number);
          const [eh, em] = slot.endTime.split(':').map(Number);
          return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
        })();
        if (existing) {
          existing.count++;
          existing.hours += slotHours;
        } else {
          instructorMap.set(slot.instructorId, {
            name: slot.instructorName || `Instructor ${slot.instructorId}`,
            count: 1,
            hours: slotHours,
          });
        }
      }

      // Topics
      if (slot.topicMasterId) {
        const existing = topicMap.get(slot.topicMasterId);
        if (existing) {
          existing.count++;
        } else {
          topicMap.set(slot.topicMasterId, {
            name: slot.topicName || `Topic ${slot.topicMasterId}`,
            count: 1,
          });
        }
      }

      // Batches
      if (slot.batchIds) {
        slot.batchIds.forEach((id) => batchSet.add(id));
      }

      // Rooms
      if (slot.roomId) {
        roomSet.add(slot.roomId);
      }

      // Centres
      if (slot.centreId) {
        centreSet.add(slot.centreId);
      }

      // Dates
      if (slot.date) {
        dateSet.add(slot.date);
      }
    }

    return {
      totalSlots: slots.length,
      totalHours: Math.round(totalHours * 10) / 10,
      uniqueInstructors: instructorMap.size,
      uniqueTopics: topicMap.size,
      uniqueBatches: batchSet.size || batchMappings.length,
      uniqueRooms: roomSet.size,
      uniqueCentres: centreSet.size,
      uniqueDates: dateSet.size,
      statusCounts: { upcoming, past },
      instructorBreakdown: Array.from(instructorMap.entries())
        .map(([id, data]) => ({ id, ...data }))
        .sort((a, b) => b.count - a.count),
      topicBreakdown: Array.from(topicMap.entries())
        .map(([id, data]) => ({ id, ...data }))
        .sort((a, b) => b.count - a.count),
    };
  }, [slots, batchMappings]);

  if (slots.length === 0) {
    return null;
  }

  const kpiCards = [
    { label: 'Total Slots', value: analytics.totalSlots, icon: '📚', colorClass: 'analytics-primary' },
    { label: 'Teaching Hours', value: `${analytics.totalHours}h`, icon: '⏱️', colorClass: 'analytics-secondary' },
    { label: 'Instructors', value: analytics.uniqueInstructors, icon: '👨‍🏫', colorClass: 'analytics-accent' },
    { label: 'Rooms', value: analytics.uniqueRooms, icon: '🏫', colorClass: 'analytics-info' },
    { label: 'Centres', value: analytics.uniqueCentres, icon: '📍', colorClass: 'analytics-warning' },
    { label: 'Batches', value: analytics.uniqueBatches, icon: '👥', colorClass: 'analytics-purple' },
    { label: 'Topics', value: analytics.uniqueTopics, icon: '📖', colorClass: 'analytics-teal' },
    { label: 'Active Days', value: analytics.uniqueDates, icon: '📅', colorClass: 'analytics-rose' },
  ];

  const upcomingPct = analytics.totalSlots > 0
    ? Math.round((analytics.statusCounts.upcoming / analytics.totalSlots) * 100)
    : 0;
  const pastPct = analytics.totalSlots > 0
    ? Math.round((analytics.statusCounts.past / analytics.totalSlots) * 100)
    : 0;

  return (
    <div className="template-analytics">
      <h3 className="template-analytics-title">Template Overview</h3>

      {/* KPI Cards */}
      <div className="template-analytics-grid">
        {kpiCards.map((kpi) => (
          <div key={kpi.label} className={`template-analytics-card ${kpi.colorClass}`}>
            <div className="analytics-icon">{kpi.icon}</div>
            <div className="analytics-content">
              <div className="analytics-value">{kpi.value}</div>
              <div className="analytics-label">{kpi.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Status Distribution */}
      <div className="template-status-overview">
        <h4>Schedule Distribution</h4>
        <div className="status-bars">
          <div className="status-bar-item">
            <div className="status-bar-label">
              <span className="status-dot upcoming"></span>
              <span>Upcoming ({analytics.statusCounts.upcoming})</span>
              <span className="status-pct">{upcomingPct}%</span>
            </div>
            <div className="status-bar-track">
              <div
                className="status-bar-fill upcoming-fill"
                style={{ width: `${upcomingPct}%` }}
              ></div>
            </div>
          </div>

          <div className="status-bar-item">
            <div className="status-bar-label">
              <span className="status-dot past"></span>
              <span>Past ({analytics.statusCounts.past})</span>
              <span className="status-pct">{pastPct}%</span>
            </div>
            <div className="status-bar-track">
              <div
                className="status-bar-fill past-fill"
                style={{ width: `${pastPct}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Instructors & Topics */}
      <div className="template-analytics-breakdown">
        {analytics.instructorBreakdown.length > 0 && (
          <div className="breakdown-section">
            <h4>Top Instructors</h4>
            <div className="breakdown-list">
              {analytics.instructorBreakdown.slice(0, 5).map((inst) => (
                <div key={inst.id} className="breakdown-item">
                  <span className="breakdown-name">{inst.name}</span>
                  <span className="breakdown-stats">
                    {inst.count} slots &middot; {Math.round(inst.hours * 10) / 10}h
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {analytics.topicBreakdown.length > 0 && (
          <div className="breakdown-section">
            <h4>Top Topics</h4>
            <div className="breakdown-list">
              {analytics.topicBreakdown.slice(0, 5).map((topic) => (
                <div key={topic.id} className="breakdown-item">
                  <span className="breakdown-name">{topic.name}</span>
                  <span className="breakdown-stats">{topic.count} slots</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateAnalytics;
